import { createSignal } from 'solid-js'

import { translate } from '../i18n'
import type { HistorySummaryEventDetail } from '../events/historySummary'

const DEFAULT_EMPTY_MESSAGE_KEY = 'No fasts recorded yet.'
const DEFAULT_LABEL_KEY = 'History'

type NormalizedSummary = {
   state: 'empty' | 'no-fasts' | 'summary'
   label: string
   description: string
   emptyMessage: string
   badgeCount: number
   formattedTotal: string
   totalGrams: number
   periodOptions: HistorySummaryEventDetail['periodOptions']
   source: 'local' | 'remote'
}

/**
 * Builds the default (empty) history-summary state.
 *
 * @returns The default summary.
 */
function createDefaultSummary(): NormalizedSummary {
   return {
      state: 'empty',
      label: translate(DEFAULT_LABEL_KEY),
      description: '',
      emptyMessage: translate(DEFAULT_EMPTY_MESSAGE_KEY),
      badgeCount: 0,
      formattedTotal: '--',
      totalGrams: 0,
      periodOptions: [],
      source: 'local',
   }
}

/**
 * Normalizes a history-summary event detail into the store's shape, applying defaults.
 *
 * @param detail - The event detail, if any.
 * @returns The normalized summary.
 */
function normalizeSummary(detail?: HistorySummaryEventDetail | null): NormalizedSummary {
   if (!detail || typeof detail !== 'object') {
      return createDefaultSummary()
   }

   const defaultSummary = createDefaultSummary()

   const state =
      detail.state === 'no-fasts' || detail.state === 'summary' ? detail.state : 'empty'
   const periodOptions = Array.isArray(detail.periodOptions) ? detail.periodOptions : []

   return {
      state,
      label:
         typeof detail.label === 'string' && detail.label ?
            detail.label
         :  defaultSummary.label,
      description:
         typeof detail.description === 'string' ?
            detail.description
         :  defaultSummary.description,
      emptyMessage:
         typeof detail.emptyMessage === 'string' && detail.emptyMessage ?
            detail.emptyMessage
         :  translate(DEFAULT_EMPTY_MESSAGE_KEY),
      badgeCount:
         typeof detail.badgeCount === 'number' && Number.isFinite(detail.badgeCount) ?
            Math.max(0, Math.round(detail.badgeCount))
         :  0,
      formattedTotal:
         typeof detail.formattedTotal === 'string' && detail.formattedTotal ?
            detail.formattedTotal
         :  defaultSummary.formattedTotal,
      totalGrams:
         typeof detail.totalGrams === 'number' && Number.isFinite(detail.totalGrams) ?
            Math.max(0, detail.totalGrams)
         :  0,
      periodOptions,
      source: detail.source === 'remote' ? 'remote' : 'local',
   }
}

const [historySummary, setHistorySummary] =
   createSignal<NormalizedSummary>(createDefaultSummary())
const [historySummaryLastUpdate, setHistorySummaryLastUpdate] = createSignal(0)

/**
 * Extracts the emitted-at timestamp from a summary detail, defaulting to now.
 *
 * @param detail - The event detail, if any.
 * @returns The emitted-at timestamp.
 */
function normalizeEmittedAt(detail?: HistorySummaryEventDetail | null): number {
   if (!detail || typeof detail !== 'object') {
      return Date.now()
   }
   return typeof detail.emittedAt === 'number' && Number.isFinite(detail.emittedAt) ?
         detail.emittedAt
      :  Date.now()
}

/**
 * Applies a history-summary update, ignoring stale (out-of-order) events.
 *
 * @param detail - The event detail, if any.
 */
export function applyHistorySummaryUpdate(detail?: HistorySummaryEventDetail | null) {
   if (!detail || typeof detail !== 'object') {
      return
   }
   const emittedAt = normalizeEmittedAt(detail)
   if (emittedAt < historySummaryLastUpdate()) {
      return
   }
   setHistorySummaryLastUpdate(emittedAt)
   setHistorySummary(normalizeSummary(detail))
}

export { historySummary }
